/**
 * RFC 6902 JSON Patch helpers shared by the EthisysCore monolith and plugins.
 *
 * The wire shape (`JsonPatchOperation`) matches the backend SDK's
 * `EthisysCore.Plugin.Sdk.JsonPatchOperation` record, so a patch produced here by
 * {@link generateJsonPatch} is applied by `IEntityPatchService` on the server.
 */
import { apply, create } from "mutative";

/** JSON Patch operation type. */
export type PatchOperation = "add" | "remove" | "replace";

/**
 * A single JSON Patch operation (RFC 6902 compatible; mutative's format with a string path).
 */
export interface JsonPatchOperation {
  /** The operation to be performed. */
  op: PatchOperation;
  /** JSON Pointer path (RFC 6902 format). */
  path: string;
  /** The value for add/replace operations. */
  value?: unknown;
  /** Source pointer for move/copy operations (backend `From`); unused by the generate/apply helpers. */
  from?: string;
}

/** Array of JSON Patch operations. */
export type JsonPatch = JsonPatchOperation[];

/**
 * Generates JSON Patch operations (RFC 6902).
 * Strictly typed, handles Date objects, and ignores key order.
 */
export function generateJsonPatch<T>(original: T, modified: T): JsonPatch {
  // If types are completely different (e.g. Array vs Object), strict replacement.
  if (!isSameType(original, modified)) {
    // If both are strictly equal (e.g. both null), returns empty
    if (original === modified) return [];
    // If different, replace root
    return [{ op: "replace", path: "", value: modified }];
  }

  const [, patches] = create(
    original,
    (draft) => {
      // We can safely cast draft to unknown here because we control the recursion
      applyChanges(draft, modified);
    },
    {
      enablePatches: {
        pathAsArray: false,
      },
    },
  );

  return patches.map(
    (patch): JsonPatchOperation => ({
      op: patch.op as "add" | "remove" | "replace",
      path: patch.path as string,
      // explicitly check for value existence to satisfy strict null checks if needed
      ...(patch.op !== "remove" ? { value: patch.value } : {}),
    }),
  );
}

/**
 * Recursively applies changes to the Mutative draft.
 * Uses 'unknown' and Type Guards instead of 'any'.
 */
function applyChanges(draft: unknown, modified: unknown): void {
  // This handles Primitives, Dates, and identical References early
  if (isDeepEqual(draft, modified)) {
    return;
  }

  // We explicitly check isArray to safely access .length and .push
  if (Array.isArray(draft) && Array.isArray(modified)) {
    const draftArray = draft as Array<unknown>;
    // Clear and Replace approach (safer for DTOs than diffing indices)
    draftArray.length = 0;
    draftArray.push(...modified);
    return;
  }

  // We use type guards to ensure we can treat them as dictionaries
  if (isPlainObject(draft) && isPlainObject(modified)) {
    const draftObj = draft;
    const modifiedObj = modified;

    // A. Remove keys that don't exist in modified
    for (const key of Object.keys(draftObj)) {
      if (!Object.prototype.hasOwnProperty.call(modifiedObj, key)) {
        delete draftObj[key];
      }
    }

    // B. Add or update top-level keys. We deliberately do NOT recurse into nested objects: the backend
    // patch applier (IEntityPatchService) supports only top-level property operations, so a changed
    // nested object is replaced wholesale (path "/key"), never diffed into "/key/child" paths that the
    // server would reject.
    for (const key of Object.keys(modifiedObj)) {
      if (!isDeepEqual(draftObj[key], modifiedObj[key])) {
        draftObj[key] = modifiedObj[key];
      }
    }
    return;
  }
}

/**
 * Handles: Primitives, Arrays, Dates, Objects (order-independent), null/undefined
 */
function isDeepEqual(a: unknown, b: unknown): boolean {
  // Strict Reference & Primitive Check
  if (a === b) return true;

  // Null/Undefined Checks
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  // Type Mismatch
  if (typeof a !== typeof b) return false;

  // Date Objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Objects
  if (typeof a === "object" && typeof b === "object") {
    // Treat as Record<string, unknown> safely
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
      if (!isDeepEqual(objA[key], objB[key])) return false;
    }
    return true;
  }

  return false;
}

/**
 * Type Guard: Checks if value is a plain object (not null, array, or Date)
 * Allows safe casting to Record<string, unknown>
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * Helper: Checks if two values share a high-level type category
 */
function isSameType(a: unknown, b: unknown): boolean {
  if (Array.isArray(a)) return Array.isArray(b);
  if (Array.isArray(b)) return false;
  if (a instanceof Date) return b instanceof Date;
  if (a && typeof a === "object" && b && typeof b === "object") return true;
  return typeof a === typeof b;
}

/**
 * Applies JSON Patch operations to an object using Mutative.
 * @param original - The original object
 * @param patches - Array of JSON Patch operations to apply
 * @returns The patched object (immutable)
 */
export function applyJsonPatch<T>(original: T, patches: JsonPatch): T {
  // Check for root replacement first - if found, it should be the only operation
  const rootReplacement = patches.find((patch) => patch.path === "" && patch.op === "replace");
  if (rootReplacement) {
    return rootReplacement.value as T;
  }

  // Filter out any root operations that might interfere and apply the rest
  const nonRootPatches = patches.filter((patch) => patch.path !== "");
  if (nonRootPatches.length === 0) {
    return original;
  }

  // Mutative's apply function expects patches in RFC 6902 format, which is what we have
  return apply(original as Record<string, unknown>, nonRootPatches) as T;
}

/**
 * Checks if a patch array is empty (null, undefined, or empty array).
 * @param patches - Array of patch operations or null/undefined
 * @returns True if patches is null, undefined, or empty array
 */
export function isNullOrEmpty(patches: JsonPatch | null | undefined): boolean {
  return !patches || patches.length === 0;
}

/**
 * Checks if a patch array has any operations.
 * @param patches - Array of patch operations
 * @returns True if patches array has operations, false if empty
 */
export function hasPatches(patches: JsonPatch): boolean {
  return patches.length > 0;
}

/**
 * Creates a deep clone of an object.
 * Useful for creating the "modified" version before generating patches.
 * @param obj - Object to clone
 * @returns Deep cloned object
 */
export function deepClone<T>(obj: T): T {
  // structuredClone preserves Dates (and undefined); fall back to JSON clone on legacy runtimes.
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}
